import { GetMuxJwt } from "connection/mux";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { useTypedSelector } from "store/reducers/use-typed-selector";
import { MuxPlaybackIdRetrieveResponse } from "types/mux-playback-policy";

type Props = {
	drmEnabled: boolean;
	muxPlaybackId: string | null;
	muxPolicyDetails: MuxPlaybackIdRetrieveResponse | null;
};

const useGetMuxThumbnail = ({
	drmEnabled,
	muxPlaybackId,
	muxPolicyDetails,
}: Props) => {
	const [fetchedMuxDrMThumbs, setFetchedMuxDrMThumbs] = useState<Record<number, string>>({});
	const [scrubSecondForThumbnail, setScrubSecondForThumbnail] = useState<number | null>(null);
	const [muxThumbSrc, setMuxThumbSrc] = useState<string | null>(null);

	const fetchMuxThumbTokenTimer = useRef<NodeJS.Timeout | null>(null);

	const adminToken = useTypedSelector(state => state.AuthReducer.token);
	const profileToken = useTypedSelector(state => state.LiveEventReducer.blProfileUserToken);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const profile = useTypedSelector(state => state.LiveEventReducer.blProfileUser);

	useEffect(() => {
		// note, do not include `token` in public policy playback id or the url will fail
		if (!drmEnabled) return;
		if (!muxPlaybackId) return;
		if (scrubSecondForThumbnail === null) return;

		// if we've already fetched, don't make another call.
		if (fetchedMuxDrMThumbs?.[scrubSecondForThumbnail]) {
			setMuxThumbSrc(fetchedMuxDrMThumbs[scrubSecondForThumbnail]);
			return;
		}

		(async () => {
			try {
				if (fetchMuxThumbTokenTimer.current) {
					clearTimeout(fetchMuxThumbTokenTimer.current);
				}
				// wrap in a small timeout so that if a user mouses over the scrub bar fast
				// we don't make every single api call
				fetchMuxThumbTokenTimer.current = setTimeout(async () => {
					const userId = user?.email || profile?.uuid || '';
					const userToken = profileToken || adminToken || '';
					const tokens = await GetMuxJwt({
						id: muxPlaybackId,
						userId,
						userToken,
						asAdmin: !profileToken && !!adminToken,
						thumbnail: true,
						drm: true,
						thumbParams: {
							time: scrubSecondForThumbnail,
							width: 160,
							height: 90,
							fit_mode: 'smartcrop',
						},
					});
					if (tokens.token) {
						const src = `https://image.mux.com/${muxPlaybackId}/thumbnail.webp?token=${tokens.token}`;
						setMuxThumbSrc(src);
						setFetchedMuxDrMThumbs(prev => ({
							...prev,
							[scrubSecondForThumbnail]: src
						}));
					}
				}, 150);
			} catch (error) {
				console.error('ERROR FETCHING MUX JWT');
				console.error(error);
			}
		})();


		return () => {
			if (fetchMuxThumbTokenTimer.current) {
				clearTimeout(fetchMuxThumbTokenTimer.current);
			}
		};
	}, [
		adminToken,
		drmEnabled,
		fetchedMuxDrMThumbs,
		muxPlaybackId,
		muxPolicyDetails?.policy,
		profile?.uuid,
		profileToken,
		scrubSecondForThumbnail,
		setMuxThumbSrc,
		user?.email,
	]);

	return useMemo(() => ({
		setScrubSecondForThumbnail,
		setMuxThumbSrc,
		muxThumbSrc
	}), [muxThumbSrc]);
};

export default useGetMuxThumbnail;
